/**
 * EmergencyContactEditFields — shared form-fields component for editing
 * an emergency-contact row. Composed by two hosts:
 *
 *   1. `EmergencyContactRowComponent` (in `emergency-contact-row.tsx`) —
 *      patient-chart inline edit-in-list. Done/Cancel/Remove action row,
 *      save deferred to tab-level "Save changes".
 *
 *   2. `EditCallBackContactView` (in `edit-callback-contact-view.tsx`) —
 *      appointment-context modal sub-view. Back/Save header, save commits
 *      via `useSavePatientEmergencyContactList` immediately.
 *
 * Mirrors Flutter `create_emeregency_contact_widget.dart` [sic — Flutter typo].
 *
 * Save-enable rule (`isEmergencyContactSaveEnabled` below): firstName,
 * lastName, contactNumber all non-blank AND `relation.id` set.
 */

import { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { Switch } from '@scripsteam/scrips-signal-ds';
import { CountryDialPicker } from '@scripsteam/scrips-signal-ds';
import { useRelations } from '@/shared/services/lookups';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import {
  formatPredictionAddress,
  deriveCityCountryFromTerms,
  useAddressSuggestions,
  type AddressPrediction,
} from '@/shared/services/use-address-suggestions';
import type { EmergencyContactRow as Row } from './emergency-contact-schema';

export interface EmergencyContactEditFieldsProps {
  row: Row;
  readOnly?: boolean;
  onChange: (next: Row) => void;
}

export function EmergencyContactEditFields({
  row,
  readOnly,
  onChange,
}: EmergencyContactEditFieldsProps) {
  // PROD-2743 · switch ON: the DS `location` glyph; it is decorative (pointer-events-none), so it
  // takes text-secondary, not the brand ink reserved for interactive things. OFF = today.
  const [relQuery, setRelQuery] = useState(row.relation.name ?? '');
  useEffect(() => {
    setRelQuery(row.relation.name ?? '');
  }, [row.relation.name]);

  const debouncedRel = useDebounce(relQuery, 250);
  const { data: relations } = useRelations(debouncedRel);
  const [relOpen, setRelOpen] = useState(false);

  // Address autocomplete (F-PAT-242). Mirrors the residential
  // `address-edit-fields` pattern. The Flutter EC widget uses a plain
  // LocationPicker (no manual-entry toggle), so we only render the search
  // picker + Apartment.
  const [addrSearch, setAddrSearch] = useState(row.address ?? '');
  useEffect(() => {
    setAddrSearch(row.address ?? '');
  }, [row.address]);
  const debouncedAddr = useDebounce(addrSearch, 300);
  const { data: addrSuggestions } = useAddressSuggestions({
    input: debouncedAddr,
    enabled: !readOnly,
  });
  const [addrOpen, setAddrOpen] = useState(false);

  const update = (patch: Partial<Row>) => onChange({ ...row, ...patch });

  // On pick: set address (main text) + derive city (terms[n-2]) and country
  // (terms[n-1]). Flutter `create_emeregency_contact_widget.dart:399-417`.
  // lat/lng stay untouched — `/api/Master/GetAddress` doesn't return coords
  // on autocomplete (same documented limitation as residential address).
  const handlePickAddress = (pred: AddressPrediction) => {
    const { mainText, city, country } = deriveCityCountryFromTerms(pred);
    update({
      address: mainText || null,
      city: city || null,
      country: country || null,
    });
    setAddrSearch(mainText);
    setAddrOpen(false);
  };

  // Keep `relationship` (string) and `relation` (object) in sync on the
  // wire — Flutter ships both and the backend parses either.
  useEffect(() => {
    if (row.relation.name !== row.relationship) {
      onChange({ ...row, relationship: row.relation.name ?? null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.relation.name]);

  return (
    <div className="grid gap-6">
      {/* Relationship typeahead — Flutter renders this as the FIRST field
          (`create_emeregency_contact_widget.dart:185`, after the photo). */}
      <div>
        <FieldLabel required>Relationship</FieldLabel>
        <div className="relative mt-1">
          <TextField
            value={relQuery}
            onChange={(e) => {
              setRelQuery(e.target.value);
              setRelOpen(true);
              // PROD-1988: typing must DROP any previously picked relation. Save needs
              // `relation.id` (Flutter's identical rule — create_edit_patient_bloc.dart:668), and
              // before this the text box and the model could disagree: pick "Friend", edit the
              // text, and the old id stayed attached to text that no longer said Friend. Now the
              // box always reflects the model — free text means no relation is chosen yet.
              if (row.relation.id) {
                onChange({ ...row, relation: { id: null, name: null }, relationship: null });
              }
            }}
            onFocus={() => setRelOpen(true)}
            onBlur={() => setTimeout(() => setRelOpen(false), 150)}
            placeholder="Enter relationship"
            disabled={readOnly}
            size="compact"
          />
          {/* Renders on focus even before anything is typed: `/api/Master/Relation` with an empty
              searchQuery returns the full list (24 entries, verified live 2026-08-20), so the
              picker is discoverable rather than something you have to guess at. Andrew hit the
              other behaviour — filled every starred field, typed the relationship, and Done
              stayed disabled with nothing saying a pick was required. */}
          {relOpen && (relations?.length ?? 0) > 0 ? (
            <ul className="absolute left-0 right-0 top-full mt-1 z-10 max-h-44 overflow-auto scrollbar-hide rounded-md border border-border-default bg-surface-card shadow-md">
              {(relations ?? []).map((r) => (
                <li key={r.id ?? r.name ?? ''}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      update({
                        relation: { id: r.id ?? null, name: r.name ?? null },
                        relationship: r.name ?? null,
                      });
                      setRelQuery(r.name ?? '');
                      setRelOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-grey-100"
                  >
                    {r.name}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        {!row.relation.id && relQuery.trim().length > 0 ? (
          <p className="mt-1 text-xs text-text-muted">
            Pick a relationship from the list — typed text alone is not saved.
          </p>
        ) : null}
      </div>

      {/* First name */}
      <div>
        <FieldLabel required>First name</FieldLabel>
        <TextField
          value={row.firstName}
          onChange={(e) => update({ firstName: e.target.value })}
          placeholder="Enter first name"
          maxLength={50}
          className="mt-1" size="compact"
          disabled={readOnly}
        />
      </div>

      {/* Middle name */}
      <div>
        <FieldLabel>Middle name</FieldLabel>
        <TextField
          value={row.middleName ?? ''}
          onChange={(e) => update({ middleName: e.target.value || null })}
          placeholder="Enter middle name"
          maxLength={50}
          className="mt-1" size="compact"
          disabled={readOnly}
        />
      </div>

      {/* Last name */}
      <div>
        <FieldLabel required>Last name</FieldLabel>
        <TextField
          value={row.lastName}
          onChange={(e) => update({ lastName: e.target.value })}
          placeholder="Enter last name"
          maxLength={50}
          className="mt-1" size="compact"
          disabled={readOnly}
        />
      </div>

      {/* Contact number */}
      <div>
        <FieldLabel required>Contact number</FieldLabel>
        <div className="mt-1">
          <CountryDialPicker
            value={row.contactNumber}
            onChange={(next) => update({ contactNumber: next })}
            disabled={readOnly}
          />
        </div>
      </div>

      {/* Address — Google Places autocomplete (F-PAT-242). Flutter
          `create_emeregency_contact_widget.dart:379-426` (LocationPicker). */}
      <div>
        <FieldLabel>Address</FieldLabel>
        <div className="relative mt-1">
          <TextField
            value={addrSearch}
            onChange={(e) => {
              setAddrSearch(e.target.value);
              setAddrOpen(true);
              // Keep the free-typed value on the row so a partial entry
              // isn't lost if the user doesn't pick a suggestion.
              update({ address: e.target.value || null });
            }}
            onFocus={() => setAddrOpen(true)}
            onBlur={() => setTimeout(() => setAddrOpen(false), 150)}
            placeholder="Enter address"
            disabled={readOnly}
            inputClassName="pr-10" size="compact"
          />
          <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-text-secondary pointer-events-none" />
          {addrOpen && (addrSuggestions?.predictions?.length ?? 0) > 0 ? (
            <ul className="absolute left-0 right-0 top-full mt-1 z-10 max-h-56 overflow-auto scrollbar-hide rounded-md border border-border-default bg-surface-card shadow-md">
              {(addrSuggestions?.predictions ?? []).map((p) => (
                <li key={p.place_id ?? p.reference}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handlePickAddress(p);
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-grey-100"
                  >
                    {formatPredictionAddress(p)}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      {/* Apartment — Flutter `:434-454` (TextField, maxLength 20). */}
      <div>
        <FieldLabel>Apartment</FieldLabel>
        <TextField
          value={row.apartment ?? ''}
          onChange={(e) => update({ apartment: e.target.value || null })}
          placeholder="Enter apartment number"
          maxLength={20}
          className="mt-1" size="compact"
          disabled={readOnly}
        />
      </div>

      {/* Primary toggle */}
      <div className="flex items-center justify-between pt-1">
        <p className="text-sm text-text-primary">Primary emergency contact</p>
        <Switch
          checked={row.isProxy}
          onCheckedChange={(checked) => update({ isProxy: checked })}
          disabled={readOnly}
          aria-label="Primary emergency contact"
        />
      </div>
    </div>
  );
}

/**
 * Save-enable rule mirrors `EmergencyContactRowComponent`'s `canDone`
 * check: firstName + lastName + contactNumber non-blank AND a relation
 * is picked (id present).
 */
/** The same rule, told the other way round — which fields are still blocking Done. Keeping it
 *  beside `isEmergencyContactSaveEnabled` is deliberate: one list, so the hint can never drift
 *  from the gate. "Relationship (pick from list)" because free text does not count. */
export function missingEmergencyContactFields(row: Row): string[] {
  const missing: string[] = [];
  if (row.firstName.trim().length === 0) missing.push('First name');
  if (row.lastName.trim().length === 0) missing.push('Last name');
  if (row.contactNumber.trim().length === 0) missing.push('Contact number');
  if (!row.relation.id) missing.push('Relationship (pick from list)');
  return missing;
}

// Graduated into Signal DS 1.45.0 — identical predicate (the three trimmed non-blanks plus
// `relation.id`), so the save-enable rule documented at the top of this file is unchanged.
export { isEmergencyContactSaveEnabled } from '@scripsteam/scrips-signal-ds';
